'use client';

import { useState, useEffect, useCallback, useRef } from 'react';
import { BlogPost } from '@prisma/client';
import { BlogCard } from '@/components/blog/BlogCard';
import { SearchBar } from '@/components/blog/SearchBar';
import { AgeRangePicker } from '@/components/blog/AgeRangePicker';
import { Utensils, ArrowLeft, Sparkles, Star, ChefHat, Heart, Clock, Users, Globe, Loader2, ChevronDown } from 'lucide-react';
import Link from 'next/link';
import { useParams, useRouter } from 'next/navigation';
import type { RecipeMetadata, SupportedLanguage } from '@/lib/recipe-types';
import { ageRangesOverlap, getAgeFilterOptions } from '@/lib/age-range-utils';

// Constants for pagination
const INITIAL_LOAD = 24; // Load 24 recipes initially (good for grid of 3-4 columns)
const LOAD_MORE_COUNT = 12; // Load 12 more on each "Load More" click

export default function EatsPage() {
  const params = useParams();
  const router = useRouter();
  const lang = (params?.lang as SupportedLanguage) || 'en';
  
  // All posts from API (full dataset for filtering)
  const [allPosts, setAllPosts] = useState<BlogPost[]>([]);
  // Filtered posts based on search/age (what would be shown)
  const [filteredPosts, setFilteredPosts] = useState<BlogPost[]>([]);
  // Currently displayed posts (paginated view)
  const [displayedPosts, setDisplayedPosts] = useState<BlogPost[]>([]);
  
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedAgeRange, setSelectedAgeRange] = useState<string | undefined>();
  const [availableAgeRanges, setAvailableAgeRanges] = useState<string[]>([]);
  const [ageRangeCounts, setAgeRangeCounts] = useState<Record<string, number>>({});
  
  // Pagination state
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  
  // Track if all data is loaded for client-side filtering
  const [allDataLoaded, setAllDataLoaded] = useState(false);
  const loadMoreRef = useRef<HTMLDivElement>(null);

  // Fetch ALL recipes for proper filtering (with high limit)
  const fetchAllPosts = useCallback(async () => {
    try {
      console.log('[EatsPage] Fetching ALL recipes from API...');
      // Fetch with high limit to get all recipes for filtering
      const response = await fetch('/api/blogs?category=EATS&limit=1000');
      
      if (!response.ok) {
        throw new Error(`API returned ${response.status}`);
      }
      
      const data = await response.json();
      console.log('[EatsPage] Total recipes in database:', data.total);
      console.log('[EatsPage] Recipes received:', data.posts?.length || 0);
      
      const fetchedPosts = data.posts || [];
      setAllPosts(fetchedPosts);
      setTotalCount(data.total || fetchedPosts.length);
      setAllDataLoaded(true);
      
      // Extract unique age ranges from ALL recipes for accurate filter options
      const ageRanges = fetchedPosts
        .map((post: BlogPost) => post.ageRange)
        .filter((range: any): range is string => !!range);
      
      console.log('[EatsPage] All age ranges from recipes:', ageRanges);
      
      // Get consolidated age filter options based on ALL recipes
      const filterOptions = getAgeFilterOptions(ageRanges);
      console.log('[EatsPage] Available filter options:', filterOptions);
      
      // Calculate counts for each age range filter
      const counts: Record<string, number> = {};
      for (const filter of filterOptions) {
        counts[filter] = fetchedPosts.filter((post: BlogPost) => {
          if (!post.ageRange) return false;
          return ageRangesOverlap(filter, post.ageRange);
        }).length;
      }
      console.log('[EatsPage] Age range counts:', counts);
      
      setAvailableAgeRanges(filterOptions);
      setAgeRangeCounts(counts);
    } catch (error) {
      console.error('[EatsPage] Error fetching posts:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAllPosts();
  }, [fetchAllPosts]);

  // Filter posts whenever filters change
  useEffect(() => {
    if (!allDataLoaded) return;
    
    let filtered = [...allPosts];
    console.log('[EatsPage] Filtering. Total posts:', filtered.length, 'Language:', lang);

    // Filter by language - show recipes with translations for the current language
    filtered = filtered.filter(post => {
      const metadata = post.metadata as RecipeMetadata;
      
      // If no metadata at all, show it (backward compatibility)
      if (!metadata) return true;
      
      // If no translations object, show it (backward compatibility)
      if (!metadata.translations) return true;
      
      // Check if translation exists for current language
      const translation = metadata.translations[lang];
      return translation && (translation.title || translation.summary);
    });
    
    console.log('[EatsPage] After language filter:', filtered.length);

    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter((post) => {
        const metadata = post.metadata as RecipeMetadata;
        const translation = metadata?.translations?.[lang];
        const title = translation?.title || post.title;
        const summary = translation?.summary || post.summary;
        
        return (
          title.toLowerCase().includes(query) ||
          summary?.toLowerCase().includes(query) ||
          post.content.toLowerCase().includes(query) ||
          post.tags?.some((tag) => tag.toLowerCase().includes(query))
        );
      });
      console.log('[EatsPage] After search filter:', filtered.length);
    }

    // Filter by age range with overlap matching
    if (selectedAgeRange) {
      filtered = filtered.filter((post) => {
        if (!post.ageRange) return false;
        return ageRangesOverlap(selectedAgeRange, post.ageRange);
      });
      console.log('[EatsPage] After age filter:', filtered.length);
    }

    console.log('[EatsPage] Final filtered count:', filtered.length);
    setFilteredPosts(filtered);
    
    // Reset pagination when filters change
    setCurrentPage(1);
    setDisplayedPosts(filtered.slice(0, INITIAL_LOAD));
    setHasMore(filtered.length > INITIAL_LOAD);
  }, [allPosts, searchQuery, selectedAgeRange, lang, allDataLoaded]);

  // Load more posts
  const loadMore = useCallback(() => {
    if (loadingMore || !hasMore) return;
    
    setLoadingMore(true);
    
    // Simulate slight delay for better UX
    setTimeout(() => {
      const nextPage = currentPage + 1;
      const endIndex = nextPage * LOAD_MORE_COUNT + INITIAL_LOAD - LOAD_MORE_COUNT;
      const newDisplayed = filteredPosts.slice(0, endIndex);
      
      setDisplayedPosts(newDisplayed);
      setCurrentPage(nextPage);
      setHasMore(newDisplayed.length < filteredPosts.length);
      setLoadingMore(false);
    }, 300);
  }, [currentPage, filteredPosts, hasMore, loadingMore]);

  const handleLanguageChange = (newLang: SupportedLanguage) => {
    router.push(`/${newLang}/blogs/eats`);
  };

  // Localized strings
  const strings: Record<SupportedLanguage, any> = {
    en: {
      backToBlogs: 'Back to Blogs',
      eats: 'Eats',
      description: 'Discover delicious recipes and healthy meals that kids absolutely love!',
      kidApproved: 'Kid-Approved',
      quickEasy: 'Quick & Easy',
      nutritious: 'Nutritious',
      searchPlaceholder: 'Search for delicious recipes...',
      loading: 'Loading delicious recipes...',
      noRecipes: 'No Recipes Found',
      noRecipesDesc: 'Try adjusting your search or filters to find delicious recipes!',
      recipesAndMeals: 'Recipes & Meals',
      showing: 'Showing',
      of: 'of',
      recipes: 'recipes',
      filteredBy: 'Filtered by',
      years: 'years',
      allAges: 'All Ages',
      loadMore: 'Load More Recipes',
      loadingMore: 'Loading...',
      more: 'more',
      allRecipesLoaded: 'You\'ve seen all recipes!',
    },
    es: {
      backToBlogs: 'Volver a Blogs',
      eats: 'Recetas',
      description: '¡Descubre recetas deliciosas y comidas saludables que los niños adoran!',
      kidApproved: 'Aprobado por Niños',
      quickEasy: 'Rápido y Fácil',
      nutritious: 'Nutritivo',
      searchPlaceholder: 'Buscar recetas deliciosas...',
      loading: 'Cargando recetas deliciosas...',
      noRecipes: 'No se Encontraron Recetas',
      noRecipesDesc: '¡Intenta ajustar tu búsqueda o filtros para encontrar recetas deliciosas!',
      recipesAndMeals: 'Recetas y Comidas',
      showing: 'Mostrando',
      of: 'de',
      recipes: 'recetas',
      filteredBy: 'Filtrado por',
      years: 'años',
      allAges: 'Todas las Edades',
      loadMore: 'Cargar Más Recetas',
      loadingMore: 'Cargando...',
      more: 'más',
      allRecipesLoaded: '¡Has visto todas las recetas!',
    },
    fr: {
      backToBlogs: 'Retour aux Blogs',
      eats: 'Recettes',
      description: 'Découvrez des recettes délicieuses et des repas sains que les enfants adorent!',
      kidApproved: 'Approuvé par les Enfants',
      quickEasy: 'Rapide et Facile',
      nutritious: 'Nutritif',
      searchPlaceholder: 'Rechercher des recettes délicieuses...',
      loading: 'Chargement des recettes délicieuses...',
      noRecipes: 'Aucune Recette Trouvée',
      noRecipesDesc: 'Essayez d\'ajuster votre recherche ou vos filtres pour trouver des recettes délicieuses!',
      recipesAndMeals: 'Recettes et Repas',
      showing: 'Affichage',
      of: 'sur',
      recipes: 'recettes',
      filteredBy: 'Filtré par',
      years: 'ans',
      allAges: 'Tous les Âges',
      loadMore: 'Charger Plus de Recettes',
      loadingMore: 'Chargement...',
      more: 'de plus',
      allRecipesLoaded: 'Vous avez vu toutes les recettes!',
    },
    de: {
      backToBlogs: 'Zurück zu Blogs',
      eats: 'Rezepte',
      description: 'Entdecken Sie köstliche Rezepte und gesunde Mahlzeiten, die Kinder lieben!',
      kidApproved: 'Von Kindern Genehmigt',
      quickEasy: 'Schnell und Einfach',
      nutritious: 'Nahrhaft',
      searchPlaceholder: 'Suche nach köstlichen Rezepten...',
      loading: 'Lade köstliche Rezepte...',
      noRecipes: 'Keine Rezepte Gefunden',
      noRecipesDesc: 'Versuchen Sie, Ihre Suche oder Filter anzupassen, um köstliche Rezepte zu finden!',
      recipesAndMeals: 'Rezepte und Mahlzeiten',
      showing: 'Zeige',
      of: 'von',
      recipes: 'Rezepte',
      filteredBy: 'Gefiltert nach',
      years: 'Jahre',
      allAges: 'Alle Altersgruppen',
      loadMore: 'Mehr Rezepte Laden',
      loadingMore: 'Wird geladen...',
      more: 'weitere',
      allRecipesLoaded: 'Sie haben alle Rezepte gesehen!',
    },
  };

  const t = strings[lang];

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(180deg, #FFF9F5 0%, #FFFCF8 50%, #FFF5EB 100%)', position: 'relative', overflow: 'hidden' }}>
      {/* Animated background orbs */}
      <div style={{
        position: 'absolute',
        top: '10%',
        left: '5%',
        width: '400px',
        height: '400px',
        background: 'radial-gradient(circle, rgba(248, 200, 160, 0.15) 0%, transparent 70%)',
        borderRadius: '50%',
        filter: 'blur(60px)',
        animation: 'float 15s ease-in-out infinite',
        pointerEvents: 'none',
      }} />
      <div style={{
        position: 'absolute',
        top: '40%',
        right: '8%',
        width: '350px',
        height: '350px',
        background: 'radial-gradient(circle, rgba(253, 185, 136, 0.2) 0%, transparent 70%)',
        borderRadius: '50%',
        filter: 'blur(60px)',
        animation: 'float 18s ease-in-out infinite 2s',
        pointerEvents: 'none',
      }} />

      {/* Hero Section */}
      <section style={{ padding: '4rem 1.5rem 4.5rem', position: 'relative', overflow: 'hidden' }}>
        {/* Floating decorative icons */}
        {[...Array(12)].map((_, i) => (
          <div
            key={i}
            style={{
              position: 'absolute',
              width: `${Math.random() * 6 + 3}px`,
              height: `${Math.random() * 6 + 3}px`,
              background: ['#F8C8A0', '#FDB988', '#FFE5CC'][i % 3],
              borderRadius: '50%',
              top: `${Math.random() * 100}%`,
              left: `${Math.random() * 100}%`,
              animation: `float ${10 + Math.random() * 5}s ease-in-out infinite ${Math.random() * 3}s`,
              opacity: 0.4,
              boxShadow: '0 0 8px currentColor',
              pointerEvents: 'none',
            }}
          />
        ))}
        
        <div className="container" style={{ maxWidth: '1280px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
          {/* Back Button + Language Switcher */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2.5rem', flexWrap: 'wrap', gap: '1rem' }}>
            <Link href="/blogs" style={{ textDecoration: 'none' }}>
              <div
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.625rem',
                  color: '#D97706',
                  fontSize: '1rem',
                  fontWeight: 700,
                  padding: '0.75rem 1.5rem',
                  background: 'rgba(255, 255, 255, 0.9)',
                  backdropFilter: 'blur(10px)',
                  borderRadius: '50px',
                  border: '2px solid rgba(248, 200, 160, 0.3)',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer',
                  boxShadow: '0 4px 16px rgba(248, 200, 160, 0.2)',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateX(-4px) scale(1.02)';
                  e.currentTarget.style.boxShadow = '0 8px 24px rgba(248, 200, 160, 0.35)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateX(0) scale(1)';
                  e.currentTarget.style.boxShadow = '0 4px 16px rgba(248, 200, 160, 0.2)';
                }}
              >
                <ArrowLeft size={20} strokeWidth={2.5} />
                {t.backToBlogs}
              </div>
            </Link>

            {/* Language Switcher */}
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              <Globe size={20} color="#D97706" style={{ marginRight: '0.5rem' }} />
              {(['en', 'es', 'fr', 'de'] as SupportedLanguage[]).map((l) => (
                <button
                  key={l}
                  onClick={() => handleLanguageChange(l)}
                  style={{
                    padding: '0.5rem 1rem',
                    borderRadius: '8px',
                    background: l === lang ? '#D97706' : 'rgba(255, 255, 255, 0.9)',
                    color: l === lang ? '#fff' : '#D97706',
                    border: `2px solid ${l === lang ? '#D97706' : 'rgba(248, 200, 160, 0.3)'}`,
                    cursor: 'pointer',
                    fontWeight: 700,
                    fontSize: '0.875rem',
                    transition: 'all 0.2s',
                    textTransform: 'uppercase',
                  }}
                  onMouseEnter={(e) => {
                    if (l !== lang) {
                      e.currentTarget.style.background = 'rgba(248, 200, 160, 0.2)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (l !== lang) {
                      e.currentTarget.style.background = 'rgba(255, 255, 255, 0.9)';
                    }
                  }}
                >
                  {l}
                </button>
              ))}
            </div>
          </div>

          <div style={{ textAlign: 'center' }}>
            {/* Icon Container */}
            <div style={{ position: 'relative', marginBottom: '2.5rem', display: 'inline-block' }}>
              {/* Pulsing glow */}
              <div style={{
                position: 'absolute',
                width: '220px',
                height: '220px',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                background: 'radial-gradient(circle, rgba(248, 200, 160, 0.4) 0%, transparent 70%)',
                filter: 'blur(40px)',
                animation: 'pulse 3s ease-in-out infinite',
                pointerEvents: 'none',
              }} />
              
              <div style={{
                width: '140px',
                height: '140px',
                borderRadius: '36px',
                background: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(10px)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto',
                boxShadow: '0 20px 60px -15px rgba(248, 200, 160, 0.6), 0 0 0 1px rgba(255, 255, 255, 0.5) inset',
                position: 'relative',
                border: '3px solid rgba(248, 200, 160, 0.3)',
                animation: 'float 4s ease-in-out infinite',
              }}>
                <div style={{
                  position: 'absolute',
                  inset: '10px',
                  borderRadius: '30px',
                  background: 'linear-gradient(135deg, #F8C8A0, #FDB988)',
                  opacity: 0.15,
                }} />
                
                <Utensils 
                  size={64} 
                  strokeWidth={2.5}
                  color="#D97706"
                  style={{
                    position: 'relative',
                    zIndex: 1,
                    filter: 'drop-shadow(0 4px 12px rgba(217, 119, 6, 0.4))',
                  }}
                />
              </div>
            </div>

            {/* Category badge */}
            <div style={{ 
              display: 'inline-flex', 
              alignItems: 'center', 
              gap: '8px', 
              background: 'linear-gradient(135deg, rgba(248, 200, 160, 0.15), rgba(255, 229, 204, 0.15))',
              padding: '8px 20px',
              borderRadius: '50px',
              border: '2px solid rgba(248, 200, 160, 0.3)',
              marginBottom: '20px',
            }}>
              <ChefHat size={16} color="#D97706" />
              <span style={{ fontSize: '0.875rem', fontWeight: 700, color: '#D97706', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                {t.recipesAndMeals}
              </span>
            </div>

            <h1
              style={{
                fontSize: 'clamp(3rem, 6vw, 5rem)',
                fontWeight: 900,
                background: 'linear-gradient(135deg, #D97706 0%, #F59E0B 50%, #FDB988 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                marginBottom: '1.5rem',
                letterSpacing: '-0.03em',
                lineHeight: 1,
              }}
            >
              {t.eats}
            </h1>
            
            <p
              style={{
                fontSize: 'clamp(1.125rem, 2.5vw, 1.5rem)',
                color: '#64748b',
                fontWeight: 500,
                maxWidth: '750px',
                margin: '0 auto 2.5rem',
                lineHeight: 1.7,
              }}
            >
              {t.description}
            </p>

            {/* Feature badges */}
            <div style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '1rem',
              justifyContent: 'center',
              alignItems: 'center',
            }}>
              {[
                { icon: <Heart size={16} />, text: t.kidApproved, color: '#EF4444' },
                { icon: <Clock size={16} />, text: t.quickEasy, color: '#F59E0B' },
                { icon: <Star size={16} />, text: t.nutritious, color: '#10B981' },
              ].map((badge, i) => (
                <div
                  key={i}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: '0.75rem 1.5rem',
                    background: 'rgba(255, 255, 255, 0.9)',
                    backdropFilter: 'blur(10px)',
                    borderRadius: '16px',
                    border: `2px solid ${badge.color}20`,
                    fontSize: '0.9375rem',
                    fontWeight: 700,
                    color: badge.color,
                    boxShadow: `0 4px 16px ${badge.color}15`,
                  }}
                >
                  {badge.icon}
                  <span>{badge.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Filters Section */}
      <section style={{ 
        padding: '2.5rem 1.5rem', 
        background: 'rgba(255, 255, 255, 0.8)',
        backdropFilter: 'blur(20px)',
        borderTop: '2px solid rgba(248, 200, 160, 0.2)',
        borderBottom: '2px solid rgba(248, 200, 160, 0.2)',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.03)',
        position: 'relative',
        zIndex: 2,
      }}>
        <div className="container" style={{ maxWidth: '1280px', margin: '0 auto' }}>
          <div style={{ marginBottom: '1.5rem' }}>
            <SearchBar
              onSearch={setSearchQuery}
              placeholder={t.searchPlaceholder}
            />
          </div>
          <AgeRangePicker
            selectedAgeRange={selectedAgeRange}
            onSelect={setSelectedAgeRange}
            availableRanges={availableAgeRanges}
            totalCount={filteredPosts.length}
            rangeCounts={ageRangeCounts}
            labels={{ allAges: t.allAges, years: t.years }}
          />
        </div>
      </section>

      {/* Posts Grid */}
      <section style={{ padding: '4rem 1.5rem', position: 'relative', zIndex: 1 }}>
        <div className="container" style={{ maxWidth: '1280px', margin: '0 auto' }}>
          {/* Recipe Count Header */}
          {!loading && filteredPosts.length > 0 && (
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '2rem',
              padding: '1rem 1.5rem',
              background: 'rgba(255, 255, 255, 0.8)',
              backdropFilter: 'blur(10px)',
              borderRadius: '16px',
              border: '2px solid rgba(248, 200, 160, 0.2)',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <ChefHat size={24} color="#D97706" />
                <span style={{ fontSize: '1rem', fontWeight: 700, color: '#374151' }}>
                  {t.showing} <span style={{ color: '#D97706' }}>{displayedPosts.length}</span> {t.of} <span style={{ color: '#D97706' }}>{filteredPosts.length}</span> {t.recipes}
                </span>
              </div>
              {selectedAgeRange && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.5rem 1rem',
                  background: 'rgba(217, 119, 6, 0.1)',
                  borderRadius: '8px',
                  fontSize: '0.875rem',
                  color: '#D97706',
                  fontWeight: 600,
                }}>
                  {t.filteredBy}: {selectedAgeRange} {t.years}
                </div>
              )}
            </div>
          )}

          {loading ? (
            <div style={{ textAlign: 'center', padding: '5rem 2rem' }}>
              <div style={{
                width: '80px',
                height: '80px',
                border: '5px solid rgba(248, 200, 160, 0.2)',
                borderTopColor: '#D97706',
                borderRadius: '50%',
                margin: '0 auto 1.5rem',
                animation: 'spin 1s linear infinite',
              }} />
              <p style={{ color: '#64748b', fontSize: '1.125rem', fontWeight: 600 }}>
                {t.loading}
              </p>
            </div>
          ) : displayedPosts.length === 0 ? (
            <div style={{
              background: 'rgba(255, 255, 255, 0.7)',
              backdropFilter: 'blur(30px)',
              borderRadius: '48px',
              padding: 'clamp(3rem, 8vw, 5rem) clamp(2rem, 5vw, 4rem)',
              textAlign: 'center',
              border: '3px solid rgba(248, 200, 160, 0.3)',
              boxShadow: '0 30px 80px -20px rgba(248, 200, 160, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.3) inset',
              position: 'relative',
              overflow: 'hidden',
            }}>
              {/* Floating sparkles */}
              {[...Array(8)].map((_, i) => (
                <div
                  key={i}
                  style={{
                    position: 'absolute',
                    width: `${Math.random() * 6 + 3}px`,
                    height: `${Math.random() * 6 + 3}px`,
                    background: '#F8C8A0',
                    borderRadius: '50%',
                    top: `${Math.random() * 100}%`,
                    left: `${Math.random() * 100}%`,
                    animation: `float ${10 + Math.random() * 5}s ease-in-out infinite ${Math.random() * 3}s`,
                    opacity: 0.4,
                  }}
                />
              ))}

              <ChefHat 
                size={80} 
                strokeWidth={2}
                color="#D97706"
                style={{
                  marginBottom: '2rem',
                  filter: 'drop-shadow(0 8px 16px rgba(217, 119, 6, 0.3))',
                  animation: 'float 4s ease-in-out infinite',
                }}
              />
              
              <h3 style={{
                fontSize: 'clamp(2rem, 5vw, 3rem)',
                fontWeight: 900,
                background: 'linear-gradient(135deg, #D97706 0%, #F59E0B 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                marginBottom: '1rem',
                letterSpacing: '-0.02em',
              }}>
                {t.noRecipes}
              </h3>
              
              <p style={{
                fontSize: 'clamp(1.125rem, 2.5vw, 1.375rem)',
                color: '#64748b',
                maxWidth: '600px',
                margin: '0 auto',
                lineHeight: 1.7,
              }}>
                {t.noRecipesDesc}
              </p>
            </div>
          ) : (
            <>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))',
                gap: '2.5rem',
              }}>
                {displayedPosts.map((post) => {
                  const metadata = post.metadata as RecipeMetadata;
                  const translation = metadata?.translations?.[lang];
                  
                  // Create a localized version of the post for BlogCard
                  // Fallback to original title/summary if translation not available
                  const localizedPost = {
                    ...post,
                    title: translation?.title || post.title,
                    summary: translation?.summary || post.summary || '',
                    category: 'EATS' as any,
                  };

                  // Pass custom href to BlogCard to ensure correct multi-language URL
                  const recipeUrl = `/${lang}/blogs/eats/${post.slug}`;

                  return (
                    <BlogCard 
                      key={post.id} 
                      post={localizedPost}
                      customHref={recipeUrl}
                    />
                  );
                })}
              </div>

              {/* Load More Button */}
              {hasMore && (
                <div 
                  ref={loadMoreRef}
                  style={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    marginTop: '3rem',
                    paddingBottom: '2rem',
                  }}
                >
                  <button
                    onClick={loadMore}
                    disabled={loadingMore}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.75rem',
                      padding: '1rem 2.5rem',
                      fontSize: '1.125rem',
                      fontWeight: 700,
                      color: '#fff',
                      background: 'linear-gradient(135deg, #D97706 0%, #F59E0B 100%)',
                      border: 'none',
                      borderRadius: '50px',
                      cursor: loadingMore ? 'wait' : 'pointer',
                      transition: 'all 0.3s ease',
                      boxShadow: '0 8px 24px rgba(217, 119, 6, 0.3)',
                      opacity: loadingMore ? 0.7 : 1,
                    }}
                    onMouseEnter={(e) => {
                      if (!loadingMore) {
                        e.currentTarget.style.transform = 'translateY(-2px) scale(1.02)';
                        e.currentTarget.style.boxShadow = '0 12px 32px rgba(217, 119, 6, 0.4)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0) scale(1)';
                      e.currentTarget.style.boxShadow = '0 8px 24px rgba(217, 119, 6, 0.3)';
                    }}
                  >
                    {loadingMore ? (
                      <>
                        <Loader2 size={20} style={{ animation: 'spin 1s linear infinite' }} />
                        {t.loadingMore}
                      </>
                    ) : (
                      <>
                        <ChevronDown size={20} />
                        {t.loadMore} ({filteredPosts.length - displayedPosts.length} {t.more})
                      </>
                    )}
                  </button>
                </div>
              )}

              {/* All Loaded Message */}
              {!hasMore && displayedPosts.length > INITIAL_LOAD && (
                <div style={{
                  textAlign: 'center',
                  marginTop: '2rem',
                  padding: '1rem',
                  color: '#64748b',
                  fontSize: '0.9375rem',
                  fontWeight: 500,
                }}>
                  ✨ {t.allRecipesLoaded}
                </div>
              )}
            </>
          )}
        </div>
      </section>

      <style jsx>{`
        @keyframes float {
          0%, 100% { transform: translateY(0) scale(1); }
          50% { transform: translateY(-20px) scale(1.02); }
        }
        
        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.8; transform: scale(1.05); }
        }
        
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
